import { GET, PATCH, POST } from '@/shared/api/requests';
import type {
  CommissioningTask,
  CreateSmartValveHubData,
  CreateSmartValveDeviceData,
  CreateSmartValveUnitData,
  CreateSmartValveZoneData,
  CreateYoLinkAccountData,
  SmartValveAreaTag,
  SmartValveChoices,
  SmartValveDevice,
  SmartValveEvent,
  SmartValveHub,
  SmartValveSummary,
  SmartValveUnit,
  SmartValveZone,
  UpdateCommissioningTaskData,
  YoLinkAccount,
} from '../types';

const BASE = '/smart-valves';

const toResults = <T>(data: unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && 'results' in data) {
    return (data as { results?: T[] }).results ?? [];
  }
  return [];
};

const projectQuery = (projectId?: number) => {
  const params = new URLSearchParams();
  if (projectId) params.set('project_id', String(projectId));
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const getSmartValveSummary = async (projectId?: number): Promise<SmartValveSummary> => {
  const response = await GET(`${BASE}/summary/${projectQuery(projectId)}`);
  return response.data as SmartValveSummary;
};

export const getSmartValveChoices = async (): Promise<SmartValveChoices> => {
  const response = await GET(`${BASE}/choices/`);
  return response.data as SmartValveChoices;
};

export const getSmartValveAreaTags = async (projectId: number): Promise<SmartValveAreaTag[]> => {
  const layoutsResponse = await GET(`projects/${projectId}/layouts/`);
  const layoutsData = layoutsResponse.data;
  const layouts = (Array.isArray(layoutsData)
    ? layoutsData
    : layoutsData?.layouts ?? layoutsData?.results ?? []) as { id: number; name: string }[];

  const responses = await Promise.all(
    layouts.map(async (layout) => ({
      layout,
      response: await GET(`area-tags/?layout_id=${layout.id}&project_id=${projectId}`),
    })),
  );

  const areaTags = responses.flatMap(({ layout, response }) => {
    const data = response.data;
    const list = (Array.isArray(data) ? data : data?.results ?? []) as { id: number; name: string }[];
    return list.map((tag) => ({
      id: tag.id,
      name: tag.name,
      layout_id: layout.id,
      layout_name: layout.name,
    }));
  });

  return Array.from(new Map(areaTags.map((tag) => [tag.id, tag])).values());
};

export const getSmartValveZones = async (projectId?: number): Promise<SmartValveZone[]> => {
  const response = await GET(`${BASE}/zones/${projectQuery(projectId)}`);
  return toResults<SmartValveZone>(response.data);
};

export const getYoLinkAccounts = async (projectId?: number): Promise<YoLinkAccount[]> => {
  const response = await GET(`${BASE}/accounts/${projectQuery(projectId)}`);
  return toResults<YoLinkAccount>(response.data);
};

export const createYoLinkAccount = async (
  payload: CreateYoLinkAccountData,
): Promise<YoLinkAccount> => {
  const response = await POST(`${BASE}/accounts/`, payload);
  return response.data as YoLinkAccount;
};

export const createSmartValveZone = async (
  payload: CreateSmartValveZoneData,
): Promise<SmartValveZone> => {
  const response = await POST(`${BASE}/zones/`, payload);
  return response.data as SmartValveZone;
};

export const getSmartValveHubs = async (projectId?: number): Promise<SmartValveHub[]> => {
  const response = await GET(`${BASE}/hubs/${projectQuery(projectId)}`);
  return toResults<SmartValveHub>(response.data);
};

export const createSmartValveHub = async (
  payload: CreateSmartValveHubData,
): Promise<SmartValveHub> => {
  const response = await POST(`${BASE}/hubs/`, payload);
  return response.data as SmartValveHub;
};

export const refreshSmartValveHub = async (
  id: number,
): Promise<{ hub: SmartValveHub }> => {
  const response = await POST(`${BASE}/hubs/${id}/refresh-status/`, {});
  return response.data as { hub: SmartValveHub };
};

export const testSmartValveZoneCredentials = async (
  zoneId: number,
): Promise<{ ok: boolean; payload: Record<string, unknown> }> => {
  const response = await POST(`${BASE}/zones/${zoneId}/test-credentials/`, {});
  return response.data as { ok: boolean; payload: Record<string, unknown> };
};

export const syncSmartValveZone = async (
  zoneId: number,
): Promise<{ created: number; updated: number; payload: Record<string, unknown> }> => {
  const response = await POST(`${BASE}/zones/${zoneId}/sync-yolink/`, {});
  return response.data as { created: number; updated: number; payload: Record<string, unknown> };
};

export const getSmartValveUnits = async (projectId?: number): Promise<SmartValveUnit[]> => {
  const response = await GET(`${BASE}/units/${projectQuery(projectId)}`);
  return toResults<SmartValveUnit>(response.data);
};

export const createSmartValveUnit = async (
  payload: CreateSmartValveUnitData,
): Promise<SmartValveUnit> => {
  const response = await POST(`${BASE}/units/`, payload);
  return response.data as SmartValveUnit;
};

export const updateSmartValveUnit = async (
  id: number,
  payload: Partial<CreateSmartValveUnitData & { status: string }>,
): Promise<SmartValveUnit> => {
  const response = await PATCH(`${BASE}/units/${id}/`, payload);
  return response.data as SmartValveUnit;
};

export const getSmartValveDevices = async (projectId?: number): Promise<SmartValveDevice[]> => {
  const response = await GET(`${BASE}/devices/${projectQuery(projectId)}`);
  return toResults<SmartValveDevice>(response.data);
};

export const createSmartValveDevice = async (
  payload: CreateSmartValveDeviceData,
): Promise<SmartValveDevice> => {
  const response = await POST(`${BASE}/devices/`, payload);
  return response.data as SmartValveDevice;
};

export const refreshSmartValveDevice = async (
  id: number,
): Promise<{ device: SmartValveDevice; payload: Record<string, unknown> }> => {
  const response = await POST(`${BASE}/devices/${id}/refresh-status/`, {});
  return response.data as { device: SmartValveDevice; payload: Record<string, unknown> };
};

export const closeSmartValve = async (
  id: number,
  payload: { create_test_record?: boolean; notes?: string } = {},
): Promise<{ device: SmartValveDevice }> => {
  const response = await POST(`${BASE}/devices/${id}/close-valve/`, payload);
  return response.data as { device: SmartValveDevice };
};

export const openSmartValve = async (
  id: number,
): Promise<{ device: SmartValveDevice; payload: Record<string, unknown> }> => {
  const response = await POST(`${BASE}/devices/${id}/open-valve/`, {});
  return response.data as { device: SmartValveDevice; payload: Record<string, unknown> };
};

export const updateCommissioningTask = async (
  id: number,
  payload: UpdateCommissioningTaskData,
): Promise<CommissioningTask> => {
  const response = await PATCH(`${BASE}/tasks/${id}/`, payload);
  return response.data as CommissioningTask;
};

export const getSmartValveEvents = async (projectId?: number): Promise<SmartValveEvent[]> => {
  const response = await GET(`${BASE}/events/${projectQuery(projectId)}`);
  return toResults<SmartValveEvent>(response.data);
};
